"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUser } from "@/hooks/useUser";
import { useAuth } from "@/lib/auth-client";
import { useI18n } from "@llmgateway/i18n/react";
interface LoginFormValues {
  email: string;
  password: string;
}
function getSafeRedirectUrl(url: string | null): string {
  if (!url) {
    return "/";
  }
  if (url.startsWith("/") && !url.startsWith("//")) {
    return url;
  }
  return "/";
}
export default function Login() {
  const $ = _c(64);
  const queryClient = useQueryClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const {
    signIn
  } = useAuth();
  const {
    t
  } = useI18n();
  let t0;
  if ($[0] !== searchParams) {
    t0 = getSafeRedirectUrl(searchParams.get("returnUrl"));
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const returnUrl = t0;
  let t1;
  if ($[2] !== t) {
    t1 = z.object({
      email: z.string().email({
        message: t("admin.auth.emailInvalid")
      }),
      password: z.string().min(1, {
        message: t("admin.auth.passwordRequired")
      })
    });
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const formSchema = t1;
  let t2;
  if ($[4] !== returnUrl) {
    t2 = {
      redirectTo: returnUrl,
      redirectWhen: "authenticated"
    };
    $[4] = returnUrl;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useUser(t2);
  let t3;
  if ($[6] !== formSchema) {
    t3 = zodResolver(formSchema);
    $[6] = formSchema;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      email: "",
      password: ""
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3) {
    t5 = {
      resolver: t3,
      defaultValues: t4
    };
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const form = useForm(t5);
  let t6;
  if ($[11] !== queryClient || $[12] !== router || $[13] !== signIn || $[14] !== t) {
    t6 = async function onSubmit(values) {
      setIsLoading(true);
      const {
        error
      } = await signIn.email({
        email: values.email,
        password: values.password
      }, {
        onSuccess: () => {
          queryClient.clear();
          toast.success(t("admin.auth.loginSuccessful"));
          router.push("/");
        },
        onError: ctx => {
          toast.error(ctx.error.message ?? t("admin.auth.unknownError"), {
            style: {
              backgroundColor: "var(--destructive)",
              color: "var(--destructive-foreground)"
            }
          });
        }
      });
      if (error) {
        toast.error(error.message ?? t("admin.auth.unknownError"), {
          style: {
            backgroundColor: "var(--destructive)",
            color: "var(--destructive-foreground)"
          }
        });
      }
      setIsLoading(false);
    };
    $[11] = queryClient;
    $[12] = router;
    $[13] = signIn;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onSubmit = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute right-4 top-4"><LanguageSwitcher /></div>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("admin.auth.welcomeBack");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <h1 className="text-2xl font-semibold tracking-tight">{t8}</h1>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t) {
    t10 = t("admin.auth.subtitle");
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <p className="text-sm text-muted-foreground">{t10}</p>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t9) {
    t12 = <div className="flex flex-col space-y-2 text-center">{t9}{t11}</div>;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== form || $[29] !== onSubmit) {
    t13 = form.handleSubmit(onSubmit);
    $[28] = form;
    $[29] = onSubmit;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t) {
    t14 = t15 => {
      const {
        field
      } = t15;
      return <FormItem><FormLabel>{t("auth.email")}</FormLabel><FormControl><Input placeholder="name@example.com" type="email" autoComplete="username webauthn" {...field} /></FormControl><FormMessage /></FormItem>;
    };
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== form.control || $[34] !== t14) {
    t15 = <FormField control={form.control} name="email" render={t14} />;
    $[33] = form.control;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t) {
    t16 = t17 => {
      const {
        field: field_0
      } = t17;
      return <FormItem><FormLabel>{t("auth.password")}</FormLabel><FormControl><Input placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"} type="password" autoComplete="current-password webauthn" {...field_0} /></FormControl><FormMessage /></FormItem>;
    };
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== form.control || $[39] !== t16) {
    t17 = <FormField control={form.control} name="password" render={t16} />;
    $[38] = form.control;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== isLoading || $[42] !== t) {
    t18 = isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("admin.auth.signingIn")}</> : t("auth.signIn");
    $[41] = isLoading;
    $[42] = t;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== isLoading || $[45] !== t18) {
    t19 = <Button type="submit" className="w-full" disabled={isLoading}>{t18}</Button>;
    $[44] = isLoading;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t13 || $[48] !== t15 || $[49] !== t17 || $[50] !== t19) {
    t20 = <form onSubmit={t13} className="space-y-4">{t15}{t17}{t19}</form>;
    $[47] = t13;
    $[48] = t15;
    $[49] = t17;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== form || $[53] !== t20) {
    t21 = <Form {...form}>{t20}</Form>;
    $[52] = form;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t) {
    t23 = t("admin.auth.or");
    $[56] = t;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t23) {
    t24 = <div className="relative">{t22}<div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">{t23}</span></div></div>;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t12 || $[61] !== t21 || $[62] !== t24) {
    t25 = <div className="px-4 sm:px-0 max-w-[64rem] mx-auto flex h-screen w-screen flex-col items-center justify-center">{t7}<div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">{t12}{t21}{t24}</div></div>;
    $[60] = t12;
    $[61] = t21;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  return t25;
}